import React, { FC } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getFlagUrl } from '../../utils/getFlagUrl';

type ResultsTableProps = {
    values: string[][];
    title?: string;
};

export const ResultsTable: FC<ResultsTableProps> = ({ values, title }) => {
    const [headers, ...rows] = values;

    const countryColIndex = headers.findIndex((h) => h.toLowerCase() === 'country');
    const qColIndex = headers.findIndex((h) => h.toLowerCase() === 'q');

    const isQualified = (value?: string) => value === 'Q';
    const isWinner = (value?: string) => value?.toLowerCase() === 'winner';

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // =========================================================
    // 📱 MOBILE VIEW
    // =========================================================
    if (isMobile) {
        const headers = values[0];
        const rows = values.slice(1);

        const colIndex = (name: string) => headers.findIndex((h) => h.toLowerCase().includes(name.toLowerCase()));

        const rankCol = colIndex('rnk');
        const bibCol = colIndex('bib');
        const firstNameCol = colIndex('first');
        const lastNameCol = colIndex('last');
        const countryCol = colIndex('country');

        // optional
        const timeCol = colIndex('time');
        const pointsCol = colIndex('points');
        const lapsCol = colIndex('laps');
        const qCol = colIndex('q');

        return (
            <Box sx={{ py: 2 }}>
                {title && (
                    <Paper sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden' }}>
                        {/* 🏷️ Title */}
                        <Box
                            sx={{
                                px: 2,
                                py: 1.5,
                                backgroundColor: 'grey.900',
                                color: 'common.white',
                            }}
                        >
                            <Typography fontWeight={600}>{title}</Typography>
                        </Box>
                    </Paper>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 2, marginBottom: 2 }}>
                    {rows.map((row, index) => {
                        const rank = row[rankCol];
                        const bib = row[bibCol];
                        const firstName = row[firstNameCol];
                        const lastName = row[lastNameCol];
                        const country = row[countryCol];

                        const value = row[timeCol] || row[pointsCol] || row[lapsCol] || '';
                        const q = row[qCol];

                        return (
                            <Paper
                                key={index}
                                sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    display: 'flex',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        gap: 1.5,
                                    }}
                                >
                                    {/* LEFT SIDE (rank + name) */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1.5,
                                            flex: 1,
                                        }}
                                    >
                                        {/* Rank */}
                                        <Typography
                                            fontWeight={700}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {rank === 'DNF' ? '' : '# '}
                                            {rank}
                                            <Typography
                                                sx={{
                                                    fontSize: '0.9rem',
                                                    lineHeight: 1.1,
                                                }}
                                            >
                                                {bib}
                                            </Typography>
                                        </Typography>

                                        {/* Name + flag */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flex: 1, // 🔥 makes this stretch nicely
                                                p: 0.5,
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Box>
                                                <Typography fontWeight={600} sx={{ lineHeight: 1.1 }}>
                                                    {lastName}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        pl: 1.5,
                                                        color: 'text.secondary',
                                                        lineHeight: 1.1,
                                                    }}
                                                >
                                                    {firstName}
                                                </Typography>
                                            </Box>

                                            <img
                                                src={getFlagUrl(country)}
                                                alt={country}
                                                className="flag-icon"
                                                style={{ width: '24px', height: '17px' }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* RIGHT SIDE (result) */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            minWidth: row[pointsCol] ? (rows[0][qCol] ? 55 : 35) : 80,
                                            justifyContent: rows[0][qCol] ? 'space-between' : 'end',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.9rem',
                                            }}
                                        >
                                            {value}
                                        </Typography>

                                        {q && <span className="qualified-badge">Q</span>}
                                    </Box>
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>
            </Box>
        );
    }

    return (
        <Paper sx={{ mb: 3, borderRadius: 2, boxShadow: 2, overflow: 'hidden', mt: 3 }}>
            {/* 🏷️ Title */}
            {title && (
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        backgroundColor: 'grey.900',
                        color: 'common.white',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Typography fontWeight={600}>{title}</Typography>
                </Box>
            )}

            {/*/* 💻 DESKTOP */}
            <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {headers.map((header, index) => (
                                <TableCell
                                    key={index}
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        backgroundColor: 'grey.900',
                                        color: 'common.white',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows.map((row, rowIndex) => (
                            <TableRow key={rowIndex} hover>
                                {headers.map((_, cellIndex) => {
                                    const cellValue = row[cellIndex];
                                    const winner = isWinner(cellValue);
                                    const qualified = cellIndex === qColIndex && isQualified(cellValue);

                                    if (qualified) {
                                        return (
                                            <TableCell key={cellIndex} align="center">
                                                <span className="qualified-badge">Q</span>
                                            </TableCell>
                                        );
                                    }

                                    if (cellIndex === countryColIndex && cellValue) {
                                        return (
                                            <TableCell key={cellIndex} align="center">
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <img
                                                        src={getFlagUrl(cellValue)}
                                                        alt={cellValue}
                                                        className="flag-icon"
                                                    />
                                                </Box>
                                            </TableCell>
                                        );
                                    }

                                    return (
                                        <TableCell
                                            key={cellIndex}
                                            align="center"
                                            sx={{
                                                color: winner ? 'success.main' : 'text.primary',
                                                fontWeight: winner || cellIndex === 0 ? 600 : 400,
                                            }}
                                        >
                                            {cellValue ?? '—'}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};
